import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from '../../../../../services/api.service';
import { AuthService } from '../../../../../services/auth.service';
import { TleLocationCatalogResponse, TleParentLocation } from './tle-location.model';

interface RawTleSubLocation {
  _id: string | { $oid: string };
  name: string;
  latitude: number;
  longitude: number;
  description?: string;
  tleLocationName?: string;
}

interface RawTleLocation {
  _id: string | { $oid: string };
  name: string;
  active?: boolean;
  tleLocationName?: string;
  sub_locations?: RawTleSubLocation[];
}

@Injectable({ providedIn: 'root' })
export class TleLocationCatalogService {
  constructor(
    private readonly apiService: ApiService,
    private readonly authService: AuthService
  ) {}

  getLocations(): Observable<TleLocationCatalogResponse> {
    const userId = this.authService.userValue?.id;
    if (!userId) {
      return of({ locations: [] });
    }

    return this.apiService.getTleLocations(String(userId)).pipe(
      map((response: any) => ({
        locations: this.mapToParentLocations(response?.tleLocations ?? []),
      }))
    );
  }

  // BE nests sub_locations under their parent but doesn't send parentId, so it
  // is derived here from the parent's own id.
  private mapToParentLocations(rawLocations: RawTleLocation[]): TleParentLocation[] {
    return rawLocations
      .filter((entry) => entry?.active !== false)
      .map((entry) => {
        const parentId = this.normalizeId(entry._id);
        return {
          id: parentId,
          name: entry.name,
          tleLocationName: entry.tleLocationName ?? entry.name,
          subLocations: (entry.sub_locations ?? []).map((sub) => ({
            id: this.normalizeId(sub._id),
            parentId,
            name: sub.name,
            latitude: sub.latitude,
            longitude: sub.longitude,
            description: sub.description,
            tleLocationName: sub.tleLocationName,
          })),
        };
      });
  }

  // Mongo ids may arrive as a plain string or an unwrapped {$oid} object.
  private normalizeId(id: string | { $oid: string }): string {
    return typeof id === 'string' ? id : id?.$oid ?? '';
  }
}
